/**
 * Student quiz details popup — quiz-wise tabs with questions and answers.
 * Used from Session Analytics → Student tab (Pre/Post Quiz Score cells).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Spinner } from "react-bootstrap";
import { fetchStudentQuizHistory } from "../../../livekit/liveQuizApi";
import { setsEqual, type QuizSlot, type StudentQuizHistoryItem } from "../../../livekit/liveQuizTypes";

type StudentQuizDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  sessionName?: string;
  studentId: string;
  studentName: string;
  slot: QuizSlot;
  topicId?: string | null;
  topicTitle?: string | null;
};

function formatQuizTime(ms: number): string {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
}

function quizTabLabel(quiz: StudentQuizHistoryItem): string {
  const slotLabel = quiz.slot === "pre" ? "Pre" : "Post";
  const topic = quiz.topic_title || "Topic";
  return `${topic} · ${slotLabel}`;
}

function QuizQuestionsPanel({ quiz }: { quiz: StudentQuizHistoryItem }) {
  const questions = quiz.questions || [];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
          p: 1.5,
          borderRadius: 1,
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
          {quiz.missed
            ? "Missed"
            : `Score: ${quiz.score}/${quiz.max_score}`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          {quiz.missed
            ? "Student did not submit this quiz"
            : formatQuizTime(quiz.submitted_at)}
        </Typography>
      </Box>

      {questions.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: 14 }}>
          Question details are not available for this quiz.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {questions.map((q, qi) => {
            const ans = Array.isArray(quiz.answers[qi]) ? quiz.answers[qi] : [];
            const correctIndices = q.correct_indices || [q.correct_index];
            const skipped = ans.length === 0;
            const isCorrect = setsEqual(ans, correctIndices);
            return (
              <Box
                key={q.id || qi}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  p: 2,
                  bgcolor: "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    alignItems: "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#0f172a",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    Q{qi + 1}. {q.text}
                  </Typography>
                  <Chip
                    size="small"
                    label={skipped ? "Skipped" : isCorrect ? "Correct" : "Wrong"}
                    sx={{
                      fontWeight: 700,
                      bgcolor: skipped ? "#e2e8f0" : isCorrect ? "#dcfce7" : "#fee2e2",
                      color: skipped ? "#475569" : isCorrect ? "#166534" : "#991b1b",
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {q.options.map((opt, oi) => {
                    const isSelected = ans.includes(oi);
                    const isCorrectOpt = correctIndices.includes(oi);
                    return (
                      <Box
                        key={`${q.id}-${oi}`}
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: isSelected
                            ? isCorrect
                              ? "#86efac"
                              : "#fca5a5"
                            : isCorrectOpt && !skipped
                              ? "#bbf7d0"
                              : "#e2e8f0",
                          bgcolor: isSelected
                            ? isCorrect
                              ? "#f0fdf4"
                              : "#fef2f2"
                            : isCorrectOpt && !skipped
                              ? "#f7fee7"
                              : "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, color: "#334155" }}>
                          {String.fromCharCode(65 + oi)}. {opt}
                          {isSelected ? (
                            <Box
                              component="span"
                              sx={{ ml: 1, fontWeight: 700, color: "#2563eb" }}
                            >
                              Student answer
                            </Box>
                          ) : null}
                          {isCorrectOpt && (!isSelected || isCorrect) ? (
                            <Box
                              component="span"
                              sx={{ ml: 1, fontWeight: 600, color: "#15803d" }}
                            >
                              Correct
                            </Box>
                          ) : null}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

const StudentQuizDetailsDialog: React.FC<StudentQuizDetailsDialogProps> = ({
  open,
  onClose,
  sessionId,
  sessionName,
  studentId,
  studentName,
  slot,
  topicId,
  topicTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quizzes, setQuizzes] = useState<StudentQuizHistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open || !sessionId || !studentId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setQuizzes([]);
    setActiveTab(0);
    void fetchStudentQuizHistory(sessionId, studentId)
      .then((rows) => {
        if (cancelled) return;
        let filtered = rows.filter((q) => (q.slot || "post") === slot);
        if (topicId) {
          filtered = filtered.filter((q) => String(q.topic_id || "") === topicId);
        }
        filtered.sort((a, b) => Number(b.submitted_at || 0) - Number(a.submitted_at || 0));
        setQuizzes(filtered);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load quiz details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, studentId, slot, topicId]);

  const slotLabel = slot === "pre" ? "Pre-Quiz" : "Post-Quiz";
  const activeQuiz = quizzes[activeTab] ?? null;

  const subtitle = useMemo(() => {
    const parts = [studentName, slotLabel];
    if (topicTitle) parts.push(topicTitle);
    if (sessionName) parts.unshift(sessionName);
    return parts.filter(Boolean).join(" · ");
  }, [studentName, slotLabel, topicTitle, sessionName]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{slotLabel} details</Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>{subtitle}</Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 280 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <Spinner animation="border" />
          </Box>
        ) : error ? (
          <Typography sx={{ color: "#dc2626" }}>{error}</Typography>
        ) : quizzes.length === 0 ? (
          <Typography sx={{ color: "#64748b", py: 4, textAlign: "center" }}>
            No {slotLabel.toLowerCase()} submissions found for this student
            {topicTitle ? ` (${topicTitle})` : ""}.
          </Typography>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(Number(value))}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: "1px solid #e2e8f0" }}
            >
              {quizzes.map((quiz, index) => (
                <Tab
                  key={quiz.push_id || index}
                  label={quizTabLabel(quiz)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                />
              ))}
            </Tabs>
            {activeQuiz ? <QuizQuestionsPanel quiz={activeQuiz} /> : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StudentQuizDetailsDialog;
